"use client"
import { useState, cloneElement } from 'react';
import { useFloating, useInteractions, useHover, FloatingPortal, Placement, shift, offset, safePolygon, flip } from '@floating-ui/react';

const Tooltip = ({
  children,
  content,
  placement = "top-start",
  maxWidth = 300,
  interactive = false,
  forceOpen = false,
  deactivate = false,
  styleManually = false
}: {
  children: React.ReactElement;
  content: React.ReactNode;
  placement?: Placement;
  interactive?: boolean;
  maxWidth?: number;
  forceOpen?: boolean;
  deactivate?: boolean;
  styleManually?: boolean;
}) => {
  const [open, setOpen] = useState(forceOpen ? true : false);
  const { refs, floatingStyles, context } = useFloating({
    open: open,
    onOpenChange: forceOpen ? undefined : setOpen,
    placement,
    middleware: [
      offset(8), 
      flip({
        fallbackPlacements: ['right', 'left-start', 'left', 'bottom-end', 'bottom', 'bottom-start', 'top-end', 'top', 'top-start'],
        fallbackStrategy: 'bestFit',
        padding: 8
      }),
      shift({ padding: 8 })
    ]
  });
  const hover = useHover(context, {
    handleClose: interactive ? safePolygon() : undefined
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
  ]);

  const isClient = typeof window !== 'undefined';

  const elementProps = {
    ref: refs.setReference,
    ...getReferenceProps(),
  };

  return (
    <>
      {cloneElement(children, elementProps)}
      {open && isClient && !deactivate && <FloatingPortal>
        <div
          ref={refs.setFloating}
          style={{ ...floatingStyles, width: `min(${maxWidth}px, 90vw)` }}
          {...getFloatingProps()}
          className={!styleManually ? "bg-[var(--accent)] text-white rounded-lg shadow-lg z-[1000] text-xs font-mono" : ""}
          // onMouseEnter={interactive ? showTooltip : undefined}
          // onMouseLeave={interactive ? hideTooltip : undefined}
        >
          {content}
        </div>
      </FloatingPortal>}
    </>
  );
};

export const useTooltip = ({placement, interactive}: {placement: Placement, interactive: boolean}) => {
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: open,
    onOpenChange: setOpen,
    placement,
    middleware: [
      offset(8), 
      flip({
        fallbackPlacements: ['right', 'left-start', 'left', 'bottom-end', 'bottom', 'bottom-start', 'top-end', 'top', 'top-start'],
        fallbackStrategy: 'bestFit',
        padding: 8
      }),
      shift({ padding: 8 })
    ]
  });
  const hover = useHover(context, {
    handleClose: interactive ? safePolygon() : undefined
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
  ]);

  return {
    ref: refs.setReference,
    floatingRef: refs.setFloating,
    getReferenceProps,
    getFloatingProps,
    floatingStyles,
    open
  }
}

export default Tooltip;
